import React, { useEffect, useState } from 'react';
import { HomeReturn, PaymentMethod } from '../clients/rust-api-client';
import { billingService } from '../clients/ApiClients';
import { Button } from '../common/Catalyst/button';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from '../common/Catalyst/dialog';
import {
  Description,
  ErrorMessage,
  Field,
  FieldGroup,
  Fieldset,
  Label,
} from '../common/Catalyst/fieldset';
import { Input } from '../common/Catalyst/input';
import { Textarea } from '../common/Catalyst/textarea';
import { Text } from '../common/Catalyst/text';
import { FieldPicker } from '../common/Custom/FieldPicker';
import ShowAlert from '../ShowAlert';
import { billingErrorMessage } from '../utils/billingHelpers';
import { TextSetting } from './BillingPolicyFields';
import { paymentMethod } from './enumLabels';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onRecorded: () => void;
  hoaId: string;
  homes: HomeReturn[];
  /** Fixed to one home when opened from that home's account. */
  fixedHomeId?: string;
}

const MONEY = /^\d+(\.\d{1,2})?$/;
const DATE = /^\d{4}-\d{2}-\d{2}$/;
/**
 * The picker's value for "no home". FieldPicker reads '' as nothing
 * chosen, but here no home is a real choice (the suspense queue), so it
 * gets its own value and maps back to '' — and so to `home_id: null`.
 */
const SUSPENSE = '__suspense__';

/**
 * Record money that arrived.
 *
 * **Leaving the home blank is a legitimate act, not a mistake.** A
 * cheque that arrives on an account number nobody can resolve still has
 * to be on the books — it lands in the suspense queue with
 * `status = unmatched` and waits for somebody to say where it belongs.
 * Forcing a home here would mean either guessing or losing the money.
 */
const RecordPaymentDialog: React.FC<Props> = ({
  isOpen,
  onClose,
  onRecorded,
  hoaId,
  homes,
  fixedHomeId,
}) => {
  const [amount, setAmount] = useState('');
  const [receivedDate, setReceivedDate] = useState('');
  const [method, setMethod] = useState<PaymentMethod>('check');
  const [homeId, setHomeId] = useState('');
  const [checkNumber, setCheckNumber] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [externalRef, setExternalRef] = useState('');
  const [batchId, setBatchId] = useState('');
  const [notes, setNotes] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setAmount('');
    setReceivedDate('');
    setMethod('check');
    setHomeId(fixedHomeId ?? '');
    setCheckNumber('');
    setAccountNumber('');
    setExternalRef('');
    setBatchId('');
    setNotes('');
    setErrors({});
    setFailure(null);
  }, [isOpen, fixedHomeId]);

  const submit = async (): Promise<void> => {
    const found: Record<string, string> = {};
    if (!MONEY.test(amount.trim())) {
      found.amount = 'Enter the amount received, like 250 or 250.00.';
    }
    if (receivedDate && !DATE.test(receivedDate)) {
      found.receivedDate = 'Use a date, or leave blank for today.';
    }
    setErrors(found);
    if (Object.keys(found).length > 0) return;

    try {
      setBusy(true);
      setFailure(null);
      await billingService.recordPayment({
        hoaId,
        paymentCreate: {
          amount: amount.trim(),
          received_date: receivedDate || null,
          method,
          home_id: homeId || null,
          check_number: checkNumber.trim() || null,
          account_number: accountNumber.trim() || null,
          external_ref: externalRef.trim() || null,
          batch_id: batchId.trim() || null,
          notes: notes.trim() || null,
        },
      });
      onRecorded();
      onClose();
    } catch (err) {
      console.error('Error recording payment:', err);
      setFailure(billingErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={isOpen} onClose={onClose} size="2xl">
      <DialogTitle>Record a payment</DialogTitle>
      <DialogBody>
        {failure && (
          <div className="mb-4">
            <ShowAlert
              alertType="error"
              alertTitle="The payment was not recorded"
              alertMessage={failure}
              isVisible={!!failure}
              onClose={() => setFailure(null)}
              timeout={14000}
              isInline
            />
          </div>
        )}

        <Fieldset>
          <FieldGroup>
            <TextSetting
              label="Amount"
              prefix="$"
              value={amount}
              error={errors.amount}
              onChange={setAmount}
            />

            <Field>
              <Label>Received</Label>
              <Description>
                The date the money arrived, not today&rsquo;s date if they
                differ — aging and late fees read it.
              </Description>
              <Input
                type="date"
                value={receivedDate}
                invalid={!!errors.receivedDate}
                onChange={(e) => setReceivedDate(e.target.value)}
                className="max-w-[14rem]"
              />
              {errors.receivedDate && (
                <ErrorMessage>{errors.receivedDate}</ErrorMessage>
              )}
            </Field>

            <Field>
              <Label>Method</Label>
              <FieldPicker
                label="Method"
                value={method}
                onChange={(v) => setMethod(v as PaymentMethod)}
                options={paymentMethod.options().map((o) => ({
                  value: o.value,
                  label: o.label,
                }))}
              />
            </Field>

            {!fixedHomeId && (
              <Field>
                <Label>Home</Label>
                <Description>
                  <strong>Optional.</strong> Leave it blank if you cannot tell
                  which home this belongs to — it goes to the suspense queue and
                  can be matched later. That is better than guessing.
                </Description>
                <FieldPicker
                  label="Home"
                  value={homeId || SUSPENSE}
                  onChange={(v) => setHomeId(v === SUSPENSE ? '' : v)}
                  options={[
                    { value: SUSPENSE, label: 'Unknown — hold in suspense' },
                    ...homes.map((h) => ({
                      value: h.id,
                      label: [h.street, h.street2].filter(Boolean).join(' '),
                    })),
                  ]}
                />
              </Field>
            )}

            {method === 'check' && (
              <Field>
                <Label>Cheque number</Label>
                <Input
                  value={checkNumber}
                  onChange={(e) => setCheckNumber(e.target.value)}
                  className="max-w-[14rem]"
                />
              </Field>
            )}

            <Field>
              <Label>Account number it arrived on</Label>
              <Description>
                The 16 digits from the coupon or the lockbox file, if you have
                them.
              </Description>
              <Input
                value={accountNumber}
                onChange={(e) => setAccountNumber(e.target.value)}
                className="max-w-[20rem]"
              />
            </Field>

            <Field>
              <Label>Bank reference</Label>
              <Description>
                The bank&rsquo;s own id for this transaction. It is what stops
                the same payment being recorded twice when a batch is fetched
                again.
              </Description>
              <Input
                value={externalRef}
                onChange={(e) => setExternalRef(e.target.value)}
                className="max-w-[20rem]"
              />
            </Field>

            <Field>
              <Label>Batch</Label>
              <Description>
                Groups a day&rsquo;s deposit together. Free text.
              </Description>
              <Input
                value={batchId}
                onChange={(e) => setBatchId(e.target.value)}
                className="max-w-[20rem]"
              />
            </Field>

            <Field>
              <Label>Notes</Label>
              <Textarea
                rows={2}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </Field>

            <Text>
              {homeId
                ? 'This applies straight away against the home’s open charges, in the association’s payment order. Anything left over is held on the payment.'
                : 'With no home, this is recorded and held in suspense. Nothing is applied until it is matched.'}
            </Text>
          </FieldGroup>
        </Fieldset>
      </DialogBody>
      <DialogActions>
        <Button plain onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button color="sky" onClick={submit} disabled={busy}>
          {busy ? 'Recording…' : 'Record payment'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RecordPaymentDialog;
